import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import { 
  Sparkles, 
  Send, 
  LogOut, 
  User, 
  Mail, 
  Mic2,
  ChevronRight,
  Wand2,
  Edit3,
  CheckCircle,
  Copy,
  ExternalLink,
  ArrowLeft,
  Palette,
  LayoutDashboard,
  Lock,
  Image,
  Download
} from 'lucide-react';
import { Button, Input, Textarea, GlassCard, Footer } from './ui';
import FileUpload from './ui/FileUpload';
import QuoteCardGenerator from './QuoteCardGenerator';
import UpgradeModal from './UpgradeModal';
import { TIERS, canSendPromotionKit, getEffectiveTier } from '../utils/tierUtils';

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function UploadPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    episode_title: '',
    guest_name: '',
    guest_email: '',
    clip: null,
    external_audiogram: null
  });
  const [useCustomQuote, setUseCustomQuote] = useState(false);
  const [customQuote, setCustomQuote] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [editingQuotes, setEditingQuotes] = useState([]);
  const [editingCaptions, setEditingCaptions] = useState([]);
  const [editingCheekyCaption, setEditingCheekyCaption] = useState('');
  const [savedQuoteCard, setSavedQuoteCard] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showQuoteCardGenerator, setShowQuoteCardGenerator] = useState(false);
  const [user, setUser] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Fetch user info with tier from API
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔍 Fetching user data with token...');
      api.get('/api/verify')
      .then(response => {
        console.log('✅ User data received:', response.data);
        const userData = {
          ...response.data,
          tier: response.data.tier || TIERS.FREE,
          subscription_expires_at: response.data.subscription_expires_at || null
        };
        console.log('👤 Setting user state:', userData);
        setUser(userData);
      })
      .catch((error) => {
        console.error('❌ Error fetching user data:', error);
        setUser({ tier: TIERS.FREE });
      });
    } else {
      console.log('⚠️ No token found, setting user to FREE tier');
      setUser({ tier: TIERS.FREE });
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClipChange = (file) => {
    setFormData(prev => ({
      ...prev,
      clip: file
    }));
    setError(null);
  };

  const handleExternalAudiogramChange = (file) => {
    setFormData(prev => ({
      ...prev,
      external_audiogram: file
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    const data = new FormData();
    data.append('episode_title', formData.episode_title);
    data.append('guest_name', formData.guest_name);
    data.append('guest_email', formData.guest_email);
    
    if (customQuote && customQuote.trim()) {
      data.append('custom_quote', customQuote.trim());
    }
    
    if (formData.clip) {
      data.append('clip', formData.clip);
    }
    
    if (formData.external_audiogram) {
      data.append('external_audiogram', formData.external_audiogram);
    }

    try {
      const response = await api.post('/api/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000
      });

      setResult(response.data);
      
      if (response.data.needs_review) {
        setReviewData({
          episode_id: response.data.episode_id,
          quotes: response.data.quotes || [],
          captions: response.data.captions || [],
          cheeky_caption: response.data.cheeky_caption || '',
          magic_link: response.data.magic_link,
          has_audiogram: response.data.has_audiogram || false,
          audiogram_filename: response.data.audiogram_filename || null,
          audiogram_quote: response.data.audiogram_quote || ''
        });
        setEditingQuotes(response.data.quotes || []);
        setEditingCaptions(response.data.captions || []);
        setEditingCheekyCaption(response.data.cheeky_caption || '');
        localStorage.setItem('current_episode_id', response.data.episode_id);
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.response) {
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else if (err.request) {
        setError('Cannot connect to server. Make sure the backend is running.');
      } else {
        setError(err.message || 'Upload failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const copyMagicLink = async () => {
    const linkToCopy = reviewData?.magic_link || result?.magic_link;
    if (linkToCopy) {
      try {
        await navigator.clipboard.writeText(linkToCopy);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleQuoteChange = (index, value) => {
    const updated = [...editingQuotes];
    updated[index] = value;
    setEditingQuotes(updated);
  };

  const handleCaptionChange = (index, value) => {
    const updated = [...editingCaptions];
    updated[index] = value;
    setEditingCaptions(updated);
  };

  const handleSendToGuest = async () => {
    if (!reviewData) return;
    
    // Tier restriction: Only Pro users can send promotion kit
    if (!canSendPromotionKit(user)) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSendingEmail(true);
    setError(null);
    
    try {
      const response = await api.post('/api/send-to-guest', {
        episode_id: reviewData.episode_id,
        quotes: editingQuotes,
        captions: editingCaptions,
        cheeky_caption: editingCheekyCaption
      }, { timeout: 30000 });
      
      if (response.data.success) {
        setResult(prev => ({
          ...prev,
          email_sent: response.data.email_sent,
          message: response.data.message
        }));
      }
    } catch (err) {
      console.error('Send email error:', err);
      setError(err.response?.data?.error || 'Failed to send email.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const handleGoBack = () => {
    setReviewData(null);
    setEditingQuotes([]);
    setEditingCaptions([]);
    setEditingCheekyCaption('');
    setSavedQuoteCard(null);
    setResult(null);
    setError(null);
  };

  // Review Page Component
  if (reviewData) {
    return (
      <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl" />

        <div className="relative z-10 px-4 py-8 max-w-5xl mx-auto">
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={handleGoBack}
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-lg font-semibold text-zinc-100">Review & Send</span>
            </div>
            <Button
              variant="ghost"
              icon={LogOut}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </motion.div>

          {/* Success Banner */}
          {result?.email_sent && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-300 font-medium">
                  {result.message || 'Email sent successfully!'}
                </span>
              </div>
            </motion.div>
          )}

          {/* Error Banner */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
              >
                <p className="text-red-400">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Magic Link Card */}
          <GlassCard className="mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-400 mb-1">Guest Share Link</p>
                <p className="text-emerald-400 text-sm truncate">{reviewData.magic_link}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={linkCopied ? CheckCircle : Copy}
                  onClick={copyMagicLink}
                >
                  {linkCopied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ExternalLink}
                  onClick={() => window.open(reviewData.magic_link, '_blank')}
                >
                  Preview
                </Button>
              </div>
            </div>
          </GlassCard>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Quote Section */}
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-zinc-100">Quote</h3>
                </div>
                <Button
                  variant={showQuoteCardGenerator ? 'primary' : 'outline'}
                  size="sm"
                  icon={Palette}
                  onClick={() => setShowQuoteCardGenerator(!showQuoteCardGenerator)}
                >
                  {showQuoteCardGenerator ? 'Editing' : 'Customize'}
                </Button>
              </div>
              <Textarea
                value={editingQuotes[0] || ''}
                onChange={(e) => handleQuoteChange(0, e.target.value)}
                rows={4}
                placeholder="Enter your quote..."
              />
            </GlassCard>

            {/* Caption Section */}
            <GlassCard>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-semibold text-zinc-100">Social Caption</h3>
              </div>
              <Textarea
                value={editingCaptions[0] || ''}
                onChange={(e) => handleCaptionChange(0, e.target.value)}
                rows={4}
                placeholder="Enter social media caption..."
              />
            </GlassCard>

            {/* Cheeky Caption */}
            {editingCheekyCaption && (
              <GlassCard className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-zinc-100">Guest Caption</h3>
                </div>
                <Textarea
                  value={editingCheekyCaption}
                  onChange={(e) => setEditingCheekyCaption(e.target.value)}
                  rows={3}
                />
              </GlassCard>
            )}

            {/* Saved Quote Card Preview */}
            {savedQuoteCard && (
              <GlassCard className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Image className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-zinc-100">Quote Card Preview</h3>
                </div>
                <div className="rounded-xl overflow-hidden border border-zinc-800 mb-4">
                  <img 
                    src={`/api/images/${savedQuoteCard.filename}`}
                    alt="Generated Quote Card"
                    className="w-full max-h-[400px] object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Download}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `/api/download/image/${savedQuoteCard.filename}`;
                      link.download = savedQuoteCard.filename;
                      link.click();
                    }}
                  >
                    Download
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Edit3}
                    onClick={() => setShowQuoteCardGenerator(true)}
                  >
                    Edit
                  </Button>
                </div>
              </GlassCard>
            )}

            {/* Audiogram Preview */}
            {reviewData.has_audiogram && reviewData.audiogram_filename && (
              <GlassCard className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <Mic2 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-zinc-100">Audiogram Preview</h3>
                </div>
                {reviewData.audiogram_quote && (
                  <div className="mb-4 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
                    <p className="text-zinc-300 italic">"{reviewData.audiogram_quote}"</p>
                  </div>
                )}
                <div className="rounded-xl overflow-hidden border border-zinc-800">
                  <video 
                    controls 
                    className="w-full max-h-[400px]"
                    src={`/api/audiograms/${reviewData.audiogram_filename}`}
                  />
                </div>
              </GlassCard>
            )}
          </div>

          {/* Quote Card Generator */}
          <AnimatePresence>
            {showQuoteCardGenerator && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8"
              >
                <QuoteCardGenerator
                  initialQuote={editingQuotes[0] || ''}
                  episodeTitle={formData.episode_title}
                  guestName={formData.guest_name}
                  episodeId={reviewData.episode_id}
                  user={user}
                  onSave={(path, filename) => {
                    setShowQuoteCardGenerator(false);
                    setSavedQuoteCard({
                      path: path,
                      filename: filename
                    });
                  }}
                  onCancel={() => setShowQuoteCardGenerator(false)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Send Button */}
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              size="xl"
              icon={canSendPromotionKit(user) ? Send : Lock}
              iconPosition="right"
              loading={sendingEmail}
              onClick={handleSendToGuest}
              disabled={!canSendPromotionKit(user)}
              className="min-w-[250px]"
            >
              {canSendPromotionKit(user) ? 'Send to Guest' : 'Upgrade to Send Promotion Kit'}
            </Button>
            
            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4 p-2 bg-gray-800 text-xs text-white rounded">
                <div>User: {user ? JSON.stringify(user, null, 2) : 'null'}</div>
                <div>Can send kit: {canSendPromotionKit(user) ? 'YES' : 'NO'}</div>
                <div>Effective tier: {user ? getEffectiveTier(user) : 'N/A'}</div>
              </div>
            )}

            {typeof window !== 'undefined' && (new URLSearchParams(window.location.search)).get('debug') === '1' && (
              <div className="mt-4 p-2 bg-gray-800 text-xs text-white rounded ml-4 space-y-2">
                <div>Effective tier: {user ? getEffectiveTier(user) : 'N/A'}</div>
                <div>Can send kit: {canSendPromotionKit(user) ? 'YES' : 'NO'}</div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { try { localStorage.setItem('force_pro', 'true'); } catch (e) {}; window.location.reload(); }}
                  >
                    Force Pro
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { try { localStorage.removeItem('force_pro'); } catch (e) {}; window.location.reload(); }}
                  >
                    Clear Override
                  </Button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Upgrade Modal */}
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={handleUpgradeClick}
          />

          {/* Footer */}
          <Footer />
        </div>
      </div>
    );
  }

  // Upload Form Page
  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="absolute top-1/4 right-0 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      
      {/* Content */}
      <motion.div 
        className="relative z-10 px-4 py-8"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {/* Header */}
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="flex items-center justify-between mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              <img 
                src="/echokit-logo.png" 
                alt="EchoKit" 
                className="h-14 w-auto"
              />
              <Button
                variant="ghost"
                icon={LayoutDashboard}
                onClick={() => navigate('/')}
              >
                Dashboard
              </Button>
            </div>
            <Button
              variant="ghost"
              icon={LogOut}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </motion.div>

          {/* Hero Section */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-100 mb-4">
              Create Promotion Kit
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Generate professional promotional assets for your podcast guests in seconds
            </p>
          </motion.div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="max-w-4xl mx-auto mb-6"
              >
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-red-400">{error}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading State */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 backdrop-blur-sm"
              >
                <GlassCard className="max-w-md w-full mx-4 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 relative">
                    <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20" />
                    <div className="absolute inset-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
                    <div className="absolute inset-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Wand2 className="w-6 h-6 text-emerald-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-100 mb-2">
                    Creating Your Promotion Kit
                  </h3>
                  <p className="text-zinc-400 mb-4">
                    Generating quotes, captions, and assets...
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>This may take 30-60 seconds</span>
                  </div>
                </GlassCard>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main Form */}
          <form onSubmit={handleSubmit}>
            <div className="max-w-4xl mx-auto">
              {/* AI Feature Banner */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-8"
              >
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 p-6">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                  <div className="relative flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-100 mb-1">
                        AI-Powered Generation
                      </h3>
                      <p className="text-zinc-400 text-sm">
                        Our AI will automatically generate compelling quotes and captions from your episode title
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Two Column Layout */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <GlassCard className="p-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column - Episode Info */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-zinc-100 mb-4 flex items-center gap-2">
                          <Mic2 className="w-5 h-5 text-emerald-400" />
                          Episode Details
                        </h3>
                        <Input
                          label="Episode Title"
                          name="episode_title"
                          value={formData.episode_title}
                          onChange={handleInputChange}
                          placeholder="Building Better Products with AI"
                          icon={Mic2}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <Input
                          label="Guest Name"
                          name="guest_name"
                          value={formData.guest_name}
                          onChange={handleInputChange}
                          placeholder="Jane Doe"
                          icon={User}
                          required
                        />
                        <Input
                          label="Guest Email"
                          name="guest_email"
                          type="email"
                          value={formData.guest_email}
                          onChange={handleInputChange}
                          placeholder="guest@example.com"
                          icon={Mail}
                          helper="This email will receive the magic link"
                          required
                        />
                      </div>
                    </div>

                    {/* Right Column - Media & Options */}
                    <div className="space-y-6">
                      <FileUpload
                        value={formData.clip}
                        onChange={handleClipChange}
                        maxDuration={60}
                        label="Video clip for generating audiogram"
                        labelSuffix="(optional)"
                      />

                      {/* External Audiogram Section */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-zinc-300">
                          Already have an audiogram upload here
                        </p>
                        <FileUpload
                          value={formData.external_audiogram}
                          onChange={handleExternalAudiogramChange}
                          maxDuration={60}
                          label=""
                          labelSuffix=""
                          className="mt-2"
                        />
                      </div>

                      {/* Custom Quote Toggle */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setUseCustomQuote(!useCustomQuote)}
                          className={`
                            w-full p-4 rounded-xl border transition-all duration-300
                            flex items-center justify-between
                            ${useCustomQuote 
                              ? 'bg-emerald-500/10 border-emerald-500/30' 
                              : 'bg-zinc-900/30 border-zinc-800 hover:border-zinc-700'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`
                              w-10 h-10 rounded-lg flex items-center justify-center
                              ${useCustomQuote ? 'bg-emerald-500/20' : 'bg-zinc-800'}
                            `}>
                              <Edit3 className={`w-5 h-5 ${useCustomQuote ? 'text-emerald-400' : 'text-zinc-400'}`} />
                            </div>
                            <div className="text-left">
                              <p className="text-zinc-100 font-medium">Custom Quote</p>
                              <p className="text-zinc-500 text-sm">Add your own quote (optional)</p>
                            </div>
                          </div>
                          <ChevronRight className={`
                            w-5 h-5 text-zinc-400 transition-transform
                            ${useCustomQuote ? 'rotate-90' : ''}
                          `} />
                        </button>

                        <AnimatePresence>
                          {useCustomQuote && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-4"
                            >
                              <Textarea
                                value={customQuote}
                                onChange={(e) => setCustomQuote(e.target.value)}
                                placeholder="Enter your custom quote here..."
                                rows={3}
                                helper="This will be used as the first quote. Other quotes will be AI-generated."
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <motion.div 
                    className="mt-8 flex justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Button
                      type="submit"
                      size="xl"
                      icon={Wand2}
                      loading={loading}
                      className="min-w-[280px]"
                    >
                      Generate Promotion Kit
                    </Button>
                  </motion.div>
                </GlassCard>
              </motion.div>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

export default UploadPage;
